import { Config } from 'config/envConfig';
import React, { useEffect, useState, useCallback } from 'react';
import {
    faBook,
    faEnvelope,
    faHeadset,
    faMoneyBillWave,
    faMessage,
    faFileExport,
    faCog,
    faReceipt,
    faFolderPlus,
    faDashboard,
} from '@fortawesome/free-solid-svg-icons';
import { AllArticles } from './allArticles';
import 'react-reflex/styles.css';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { CollapsedSideBar } from '../collapsedSideBar';
import { SupportSideBar } from './supportSidebar';
import { SupportBeforeSelect } from './supportBeforeSelect';
import { AlertType, Button } from '@quyva/shared-ui';
import Navbar from '../navbar';
import { useLocation, useNavigate } from 'react-router';
import { useGlobalCredits } from '../../context/GlobalStateContext';
import TransactionList from '../Transactionlist';
import { getUrl } from '../pdfDisplay/utils';
import { Features, SupportFeatures, isFeatureEnabled } from '@quyva/common';
import { useFeatureFlags } from '../../context';
import { ChatAssistant } from './chatAssistant';
import { faProductHunt } from '@fortawesome/free-brands-svg-icons';
import { isMobile as deviceIsMobile } from 'react-device-detect';
import { DataType } from '../../enums';
import { LoadingView } from '@quyva/shared-ui';

const ICON_MAP = [
    faBook,
    faMoneyBillWave,
    faFolderPlus,
    faMessage,
    faFileExport,
    faReceipt,
    faDashboard,
    faCog,
    faProductHunt,
];

// ✅ fetch help data from backend
const getHelpSupportData = async (sectionId = null) => {
    const user = JSON.parse(localStorage.getItem('current'));
    const encodedToken = encodeURIComponent(user.token);

    try {
        const isValidSection =
            typeof sectionId === 'string' && isNaN(Number(sectionId));

        const url = isValidSection
            ? `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/helpAndSupport/${sectionId}`
            : `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/helpAndSupport/index`;

        const response = await fetch(getUrl(url), {
            method: 'GET',
            headers: {
                Authorization: encodedToken,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        if (!isValidSection) {
            return await response.json();
        }

        const html = await response.text();
        // Help images are embedded with a root-anchored src ("/help_images/..."),
        // which only resolves when the app shares the Node server's origin (local dev).
        // On hosted setups the Node server sits behind a path prefix (e.g. /nodejs via
        // Caddy), so re-anchor the src to the same Node base URL used for this fetch.
        const nodeBase = getUrl(
            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}`
        ).replace(/\/+$/, '');
        return html.replace(
            /src="\/help_images\//g,
            `src="${nodeBase}/help_images/`
        );
    } catch (error) {
        alert(
            'Failed to load help & support data. Please try again later.',
            AlertType.ERROR
        );
        return null;
    }
};

export const Support = ({
    newNotification,
    setNewNotification,
    viewNotifications,
    setViewNotifications,
    messages,
    socket,
    isSideViewExpanded,
    setIsSideViewExpanded,
    displayFloatingButton,
    setDisplayFloatingButton,
    planName,
}) => {
    const { featureFlagsData } = useFeatureFlags();
    const [activeSection, setActiveSection] = useState(null);
    const [queriesData, setQueriesData] = useState([]);
    const [helpSupportData, setHelpSupportData] = useState(null);
    const [topics, setTopics] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { transactionMode } = useGlobalCredits();

    const navigate = useNavigate();
    const location = useLocation();
    const path = location.pathname;

    // ✅ Fetch data
    const fetchData = useCallback(async () => {
        setLoading(true);

        const indexData = await getHelpSupportData(); // NO ARG = index.json

        if (indexData?.order && indexData?.sections) {
            const sidebarTopics = indexData.order.map((id, index) => ({
                id,
                name: indexData.sections[id].title,
                icon: ICON_MAP[index % ICON_MAP.length],
            }));

            setTopics(sidebarTopics);
        }

        setLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
        setIsMobile(deviceIsMobile);
    }, [fetchData]);

    const buildQueriesFromHtml = (html) => {
        const sectionRegex = /<section[^>]*>([\s\S]*?)<\/section>/gi;
        const queries = [];

        let match;
        while ((match = sectionRegex.exec(html)) !== null) {
            const sectionHtml = match[0];

            const titleMatch = sectionHtml.match(/<h1[^>]*>(.*?)<\/h1>/i);
            const title = titleMatch ? titleMatch[1] : 'Section';

            const bodyHtml = sectionHtml.replace(/<h1[^>]*>.*?<\/h1>/i, '');

            queries.push({
                query: title,
                solution: { __html: bodyHtml },
            });
        }

        return queries;
    };

    const handleSectionSelect = async (sectionId) => {
        setLoading(true);
        setActiveSection(sectionId);

        const html = await getHelpSupportData(sectionId);
        const queries = buildQueriesFromHtml(html);

        setQueriesData(queries);
        setLoading(false);
    };

    return (
        <div className='z-0 w-[99vw] h-[99vh] m-auto pt-2'>
            <ReflexContainer orientation='vertical'>
                {!isMobile && (
                    <ReflexElement
                        className='left-pane mr-0.5'
                        minSize={isSideViewExpanded ? 250 : 50}
                        size={isSideViewExpanded ? 350 : 50}
                        maxSize={isSideViewExpanded ? 700 : 50}
                    >
                        <div className='pane-content w-full h-full bg-transparent'>
                            {isSideViewExpanded ? (
                                <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl p-2 z-10'>
                                    <SupportSideBar
                                        topics={topics}
                                        activeSection={activeSection}
                                        onSectionSelect={handleSectionSelect}
                                        setIsLeftViewExpanded={
                                            setIsSideViewExpanded
                                        }
                                        displayFloatingButton={
                                            displayFloatingButton
                                        }
                                        setDisplayFloatingButton={
                                            setDisplayFloatingButton
                                        }
                                        planName={planName}
                                    />
                                </div>
                            ) : (
                                <CollapsedSideBar
                                    icon={faHeadset}
                                    iconColor={'icons-primary'}
                                    title={'Help Topics'}
                                    setIsRepoViewExpanded={
                                        setIsSideViewExpanded
                                    }
                                    planName={planName}
                                />
                            )}
                        </div>
                    </ReflexElement>
                )}

                <ReflexSplitter
                    style={{
                        border: 0,
                        background: 'white',
                        width: '6px',
                        zIndex: 10,
                        height: '98vh',
                    }}
                />

                <ReflexElement className='right-pane ml-0.5'>
                    <div className='pane-content w-full h-full bg-transparent'>
                        <div className='w-full h-full border border-2 overflow-auto bg-backgrounds-slate rounded-xl z-0'>
                            <ReflexContainer orientation='horizontal'>
                                <ReflexElement
                                    className='header'
                                    style={{
                                        minHeight: '4rem',
                                        height: '4rem',
                                        maxHeight: '4rem',
                                    }}
                                >
                                    <div className='pane-content w-full h-full p-2'>
                                        <Navbar
                                            topics={topics}
                                            activeSection={activeSection}
                                            setActiveSection={setActiveSection}
                                            onSectionSelect={
                                                handleSectionSelect
                                            }
                                            setIsLeftViewExpanded={
                                                setIsSideViewExpanded
                                            }
                                            newNotification={newNotification}
                                            setNewNotification={
                                                setNewNotification
                                            }
                                            viewNotifications={
                                                viewNotifications
                                            }
                                            setViewNotifications={
                                                setViewNotifications
                                            }
                                            messages={messages}
                                            socket={socket}
                                            displayFloatingButton={
                                                displayFloatingButton
                                            }
                                            setDisplayFloatingButton={
                                                setDisplayFloatingButton
                                            }
                                        />
                                    </div>
                                </ReflexElement>

                                <ReflexElement flex={1}>
                                    <div className='pane-content h-full w-full p-2'>
                                        {transactionMode ? (
                                            <TransactionList />
                                        ) : isLoading ? (
                                            <LoadingView loadingText='Loading help content...' />
                                        ) : activeSection ? (
                                            <AllArticles
                                                queriesData={queriesData}
                                            />
                                        ) : (
                                            <SupportBeforeSelect />
                                        )}

                                        <div className='fixed z-50 bottom-6 right-24 flex flex-col gap-3 items-end'>
                                            {isFeatureEnabled(
                                                [
                                                    Features.SUPPORT,
                                                    SupportFeatures.CONTACT_US,
                                                ],
                                                featureFlagsData
                                            ) &&
                                                displayFloatingButton && (
                                                    <Button
                                                        onClick={() => {
                                                            if (
                                                                path !==
                                                                '/contactUs'
                                                            )
                                                                navigate(
                                                                    '/contactUs'
                                                                );
                                                        }}
                                                        icon={faEnvelope}
                                                        hoverText={
                                                            'Contact us directly'
                                                        }
                                                        type={'success'}
                                                        className={
                                                            'text-lg px-6 py-6'
                                                        }
                                                    />
                                                )}

                                            <ChatAssistant
                                                socket={socket}
                                                messages={messages}
                                            />
                                        </div>
                                    </div>
                                </ReflexElement>
                            </ReflexContainer>
                        </div>
                    </div>
                </ReflexElement>
            </ReflexContainer>
        </div>
    );
};
